<template>
    <div class="import-job-progress">
        <div v-if="showContext" class="import-job-progress__context mb-2">
            <div>
                <span>Manifest</span>
                <strong>{{ manifestName || '—' }}</strong>
            </div>
            <div>
                <span>Version</span>
                <strong>{{ versionLabel || '—' }}</strong>
            </div>
            <div>
                <span>Source</span>
                <strong>{{ sourceName || '—' }}</strong>
            </div>
        </div>

        <div class="import-job-progress__heading mb-2">
            <div class="d-flex align-items-start">
                <span class="import-job-progress__status-icon mr-1" :class="statusIconClass">
                    <b-spinner v-if="isRunning" small />
                    <feather-icon v-else :icon="statusIcon" size="18" />
                </span>
                <div>
                    <div class="d-flex align-items-center mb-25">
                        <h4 class="mb-0 mr-75">{{ statusTitle }}</h4>
                        <b-badge :variant="statusVariant">{{ job.status }}</b-badge>
                    </div>
                    <div class="text-muted">{{ statusDescription }}</div>
                </div>
            </div>
            <div class="d-flex align-items-center">
                <b-button
                    v-if="!isTerminal"
                    variant="outline-secondary"
                    :disabled="refreshing"
                    @click="$emit('refresh')"
                >
                    <b-spinner v-if="refreshing" small class="mr-50" />
                    <feather-icon v-else icon="RefreshCwIcon" size="16" class="mr-50" />
                    Refresh
                </b-button>
                <b-button v-else variant="primary" @click="$emit('restart')">
                    {{ completed ? 'Run another import' : 'Back to preparation' }}
                </b-button>
            </div>
        </div>

        <b-alert v-if="pollError" variant="warning" show class="mb-2">
            <div class="alert-body d-flex align-items-center justify-content-between">
                <span>{{ pollError }}</span>
                <b-button size="sm" variant="outline-warning" @click="$emit('refresh')">
                    Retry now
                </b-button>
            </div>
        </b-alert>

        <b-alert v-if="failed" variant="danger" show class="mb-2">
            <div class="alert-body">
                <div class="font-weight-bold mb-25">
                    {{ job.failureMessage || 'The import could not be completed.' }}
                </div>
                <div v-if="failureMetadata" class="font-small-2">
                    {{ failureMetadata }}
                </div>
            </div>
        </b-alert>

        <section class="import-job-progress__progress mb-2">
            <div class="import-job-progress__progress-row">
                <div class="d-flex justify-content-between align-items-center mb-50">
                    <div>
                        <strong>Processed</strong>
                        <small class="text-muted ml-50">current attempt</small>
                    </div>
                    <span>{{ processedWeight }} / {{ totalWeight }} · {{ processedPercent }}%</span>
                </div>
                <b-progress :max="progressMax" height=".65rem">
                    <b-progress-bar variant="primary" :value="processedWeight" />
                </b-progress>
            </div>

            <div class="import-job-progress__progress-row">
                <div class="d-flex justify-content-between align-items-center mb-50">
                    <div>
                        <strong>Committed</strong>
                        <small class="text-muted ml-50">durable result</small>
                    </div>
                    <span>{{ committedWeight }} / {{ totalWeight }} · {{ committedPercent }}%</span>
                </div>
                <b-progress :max="progressMax" height=".65rem">
                    <b-progress-bar variant="success" :value="committedWeight" />
                </b-progress>
            </div>

            <div v-if="hasPendingWork" class="import-job-progress__atomic-note">
                <feather-icon icon="InfoIcon" size="16" class="mr-50" />
                Processed changes are still inside the active transaction. They become durable only after commit.
            </div>
        </section>

        <div class="import-job-progress__facts mb-2">
            <article>
                <span>Attempt</span>
                <strong>{{ currentAttempt }}</strong>
            </article>
            <article>
                <span>Units processed</span>
                <strong>{{ processedUnits }} / {{ totalUnits }}</strong>
            </article>
            <article>
                <span>Units committed</span>
                <strong>{{ committedUnits }} / {{ totalUnits }}</strong>
            </article>
            <article>
                <span>Groups processed</span>
                <strong>{{ processedGroups }} / {{ totalGroups }}</strong>
            </article>
            <article>
                <span>Groups committed</span>
                <strong>{{ committedGroups }} / {{ totalGroups }}</strong>
            </article>
        </div>

        <section v-if="activityStatistics.length" class="mb-2">
            <div class="d-flex align-items-center justify-content-between mb-1">
                <div>
                    <h5 class="mb-25">{{ statisticsTitle }}</h5>
                    <small v-if="!isTerminal" class="text-muted">
                        Live counters are provisional until the transaction commits.
                    </small>
                </div>
            </div>
            <div class="import-job-progress__statistics">
                <article v-for="statistic in activityStatistics" :key="statistic.key">
                    <span>{{ statistic.label }}</span>
                    <strong>{{ statistic.value }}</strong>
                </article>
            </div>
        </section>

        <section v-if="committedStatistics.length && !isTerminal" class="mb-2">
            <h5 class="mb-1">Committed statistics</h5>
            <div class="import-job-progress__statistics">
                <article v-for="statistic in committedStatistics" :key="statistic.key">
                    <span>{{ statistic.label }}</span>
                    <strong>{{ statistic.value }}</strong>
                </article>
            </div>
        </section>

        <b-alert v-if="warnings.length" variant="warning" show class="mb-0">
            <div class="alert-body">
                <div class="font-weight-bold mb-50">Warnings</div>
                <div v-for="(warning, index) in warnings" :key="index">{{ warning }}</div>
            </div>
        </b-alert>
    </div>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BProgress,
    BProgressBar,
    BSpinner,
} from 'bootstrap-vue'

const TERMINAL_STATUSES = ['completed', 'failed']

export default {
    name: 'ImportJobProgress',
    components: {
        BAlert,
        BBadge,
        BButton,
        BProgress,
        BProgressBar,
        BSpinner,
    },
    props: {
        job: {
            type: Object,
            required: true,
        },
        manifestName: {
            type: String,
            default: '',
        },
        versionLabel: {
            type: String,
            default: '',
        },
        sourceName: {
            type: String,
            default: '',
        },
        refreshing: {
            type: Boolean,
            default: false,
        },
        pollError: {
            type: String,
            default: null,
        },
    },
    computed: {
        liveProgress() {
            return this.job.liveProgress || null
        },
        completed() {
            return this.job.status === 'completed'
        },
        failed() {
            return this.job.status === 'failed'
        },
        isRunning() {
            return ['queued', 'importing'].includes(this.job.status)
        },
        isTerminal() {
            return TERMINAL_STATUSES.includes(this.job.status)
        },
        showContext() {
            return Boolean(this.manifestName || this.versionLabel || this.sourceName)
        },
        statusTitle() {
            return {
                queued: 'Import queued',
                importing: 'Import in progress',
                completed: 'Import completed',
                failed: 'Import failed',
            }[this.job.status] || 'Import status'
        },
        statusDescription() {
            return {
                queued: 'The execution request is waiting for an import worker.',
                importing: 'The worker is applying the frozen and validated import plan.',
                completed: 'All changes were committed successfully.',
                failed: 'No further execution attempts are running for this job.',
            }[this.job.status] || 'Waiting for the latest permanent job state.'
        },
        statusVariant() {
            return {
                queued: 'light-secondary',
                importing: 'light-primary',
                completed: 'light-success',
                failed: 'light-danger',
            }[this.job.status] || 'light-secondary'
        },
        statusIcon() {
            return this.completed ? 'CheckIcon' : this.failed ? 'XIcon' : 'ClockIcon'
        },
        statusIconClass() {
            return {
                'import-job-progress__status-icon--running': this.isRunning,
                'import-job-progress__status-icon--completed': this.completed,
                'import-job-progress__status-icon--failed': this.failed,
            }
        },
        liveWeight() {
            return this.liveProgress && this.liveProgress.weight
                ? this.liveProgress.weight
                : {}
        },
        totalWeight() {
            return Number(this.liveWeight.total || this.job.totalProgressWeight || 0)
        },
        processedWeight() {
            if (this.completed) {
                return this.totalWeight
            }
            return Number(this.liveWeight.processed || 0)
        },
        committedWeight() {
            if (this.completed) {
                return this.totalWeight
            }
            return Number(this.liveWeight.committed || 0)
        },
        progressMax() {
            return Math.max(this.totalWeight, 1)
        },
        processedPercent() {
            return this.percentage(this.processedWeight, this.totalWeight)
        },
        committedPercent() {
            return this.percentage(this.committedWeight, this.totalWeight)
        },
        hasPendingWork() {
            return !this.isTerminal && this.processedWeight > this.committedWeight
        },
        liveUnits() {
            return this.liveProgress && this.liveProgress.units
                ? this.liveProgress.units
                : {}
        },
        totalUnits() {
            return Number(this.liveUnits.total || this.job.totalUnits || 0)
        },
        processedUnits() {
            return this.completed ? this.totalUnits : Number(this.liveUnits.processed || 0)
        },
        committedUnits() {
            return this.completed ? this.totalUnits : Number(this.liveUnits.committed || 0)
        },
        liveGroups() {
            return this.liveProgress && this.liveProgress.groups
                ? this.liveProgress.groups
                : {}
        },
        totalGroups() {
            return Number(this.liveGroups.total || this.job.totalGroups || 0)
        },
        committedGroups() {
            return this.completed ? this.totalGroups : Number(this.liveGroups.committed || 0)
        },
        processedGroups() {
            return this.completed ? this.totalGroups : Number(this.liveGroups.processed || 0)
        },
        currentAttempt() {
            return Number(
                this.liveProgress && this.liveProgress.attempt
                    ? this.liveProgress.attempt
                    : this.job.attempts || 0
            )
        },
        activityStatistics() {
            const statistics = this.isTerminal
                ? this.job.statistics
                : this.liveProgress && this.liveProgress.statistics
            return this.statisticCards(statistics)
        },
        committedStatistics() {
            return this.statisticCards(
                this.liveProgress && this.liveProgress.committedStatistics
            )
        },
        statisticsTitle() {
            return this.isTerminal ? 'Final statistics' : 'Live activity'
        },
        warnings() {
            const warnings = this.isTerminal
                ? this.job.warnings
                : this.liveProgress && this.liveProgress.warnings
                    ? this.liveProgress.warnings
                    : this.job.warnings
            return Array.isArray(warnings) ? warnings : []
        },
        failureMetadata() {
            return [this.job.failureKind, this.job.failureCode].filter(Boolean).join(' · ')
        },
    },
    methods: {
        percentage(value, total) {
            if (!total) {
                return this.completed ? 100 : 0
            }
            return Math.min(100, Math.round((value / total) * 100))
        },
        statisticCards(statistics) {
            return Object.entries(statistics || {}).map(([key, value]) => ({
                key,
                label: this.humanizeKey(key),
                value,
            }))
        },
        humanizeKey(key) {
            const label = key
                .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                .replace(/[_-]+/g, ' ')
            return label.charAt(0).toUpperCase() + label.slice(1)
        },
    },
}
</script>

<style lang="scss" scoped>
.import-job-progress__context {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    overflow: hidden;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #f8f8f8;
}

.import-job-progress__context > div {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: .25rem;
    padding: .75rem 1rem;
}

.import-job-progress__context > div + div {
    border-left: 1px solid #ebe9f1;
}

.import-job-progress__context span,
.import-job-progress__facts span,
.import-job-progress__statistics span {
    color: var(--manifest-secondary-text-color, #45434c);
    font-size: .8rem;
}

.import-job-progress__context strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.import-job-progress__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.import-job-progress__status-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 2.5rem;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
}

.import-job-progress__status-icon--running {
    color: #7367f0;
    background: rgba(115, 103, 240, .12);
}

.import-job-progress__status-icon--completed {
    color: #28c76f;
    background: rgba(40, 199, 111, .12);
}

.import-job-progress__status-icon--failed {
    color: #ea5455;
    background: rgba(234, 84, 85, .12);
}

.import-job-progress__progress {
    padding: 1rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.import-job-progress__progress-row + .import-job-progress__progress-row {
    margin-top: 1rem;
}

.import-job-progress__atomic-note {
    display: flex;
    align-items: center;
    margin-top: 1rem;
    padding: .75rem;
    color: #6e6b7b;
    border-radius: .357rem;
    background: rgba(115, 103, 240, .07);
}

.import-job-progress__facts,
.import-job-progress__statistics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
    gap: .75rem;
}

.import-job-progress__facts article,
.import-job-progress__statistics article {
    padding: .75rem 1rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #f8f8f8;
}

.import-job-progress__facts strong,
.import-job-progress__statistics strong {
    display: block;
    margin-top: .25rem;
    color: #45434c;
    font-size: 1.25rem;
}

@media (max-width: 767.98px) {
    .import-job-progress__context {
        grid-template-columns: 1fr;
    }

    .import-job-progress__context > div + div {
        border-top: 1px solid #ebe9f1;
        border-left: 0;
    }

    .import-job-progress__heading {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
